import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/components/feedback/snackbar';
import { odooApiService } from '@/services/odoo.service';
import type { OdooResourceType, CreateInOdooResult } from '@/types/odoo.types';

interface UseOdooPushOptions {
  /** The Odoo entity type. */
  resourceType: OdooResourceType;
  /** Called after a successful push operation. */
  onSuccess?: (result: CreateInOdooResult) => void;
}

/**
 * Hook that provides a mutation to create a Hub resource in Odoo (push-to-odoo).
 */
export function useOdooPush({ resourceType, onSuccess }: UseOdooPushOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, mutateAsync, isLoading, error } = useMutation({
    mutationFn: (resourceId: string) => odooApiService.pushToOdoo(resourceType, resourceId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['odoo-search', resourceType] });
      queryClient.invalidateQueries({ queryKey: ['odoo-unlinked', resourceType] });
      enqueueSnackbar(t('Resource created in Odoo successfully'), { variant: 'success' });
      onSuccess?.(result);
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const rawMessage = error?.response?.data?.message ?? error?.message;
      const message =
        (typeof err === 'string' && err) ||
        (Array.isArray(rawMessage) ? rawMessage[0] : rawMessage) ||
        t('An error occurred while creating in Odoo');
      enqueueSnackbar(message, { variant: 'error' });
      Sentry.captureException(err, {
        tags: { hook: 'useOdooPush', action: 'pushToOdoo', resourceType },
      });
    },
  });

  return {
    /** Trigger the push mutation (fire-and-forget). */
    push: mutate,
    /** Trigger the push mutation (returns a promise). */
    pushAsync: mutateAsync,
    /** True while the mutation is in progress. */
    isLoading,
    /** Error from the mutation, if any. */
    error,
  };
}
